// src/utils/auth.js

import { appendCsrfHeader } from "./csrf";
import {
  isClientBasicsComplete,
  resolveClientCompletionFlag,
} from "./clientProfile";
import { resolveRegistrationRoute } from "./registration";

const ACCESS_TOKEN_KEY = "accessToken";
const REFRESH_TOKEN_KEY = "refreshToken";
const AUTH_MODE = String(process.env.REACT_APP_AUTH_MODE || "cookie")
  .trim()
  .toLowerCase();

const AUTH_STORAGE_KEYS = [
  ACCESS_TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  "tempAccessToken",
  "tempRefreshToken",
  "tempSessionToken",
  "user",
  "userEmail",
  "isGoogleSignedIn",
  // Backward compatibility: keys used in older flows
  "access",
  "refresh",
];

const EXPLICIT_LOGOUT_KEY = "pm_explicit_logout_at";

export function markExplicitLogout() {
  try {
    if (typeof sessionStorage !== "undefined") {
      sessionStorage.setItem(EXPLICIT_LOGOUT_KEY, String(Date.now()));
    }
  } catch (_) {}
}

export function clearExplicitLogoutMark() {
  try {
    if (typeof sessionStorage !== "undefined") {
      sessionStorage.removeItem(EXPLICIT_LOGOUT_KEY);
    }
  } catch (_) {}
}

export function hasExplicitLogoutMark() {
  try {
    if (typeof sessionStorage === "undefined") return false;
    return !!sessionStorage.getItem(EXPLICIT_LOGOUT_KEY);
  } catch (_) {
    return false;
  }
}

function resolveApiUrl(path) {
  const raw = String(path || "").replace(/^\/+/, "");
  const prefixed = raw.startsWith("api/") ? raw : `api/${raw}`;
  const base = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
  return base ? `${base}/${prefixed}` : `/${prefixed}`;
}

export function isTokenAuthEnabled() {
  return AUTH_MODE === "token";
}

export function clearStoredAuthTokens() {
  try {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem("tempAccessToken");
    localStorage.removeItem("tempRefreshToken");
  } catch (_) {}
}

export function initializeAuthStorage() {
  if (!isTokenAuthEnabled()) {
    clearStoredAuthTokens();
  }
}

export function clearStoredAuthData({ preserveMusaEmail = true } = {}) {
  AUTH_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
  if (!preserveMusaEmail) localStorage.removeItem("musa_email");
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent("pm:auth:cleared", {
        detail: { preserveMusaEmail },
      })
    );
  }
}

export function isAuthenticated() {
  return !!getUser();
}

function extractTokenPair(raw = {}) {
  const accessToken =
    raw.accessToken || raw.access_token || raw.access || null;
  const refreshToken =
    raw.refreshToken || raw.refresh_token || raw.refresh || null;
  return { accessToken, refreshToken };
}

export function persistAuthTokens(rawTokens = {}) {
  if (!isTokenAuthEnabled()) {
    clearStoredAuthTokens();
    return;
  }
  const { accessToken, refreshToken } = extractTokenPair(rawTokens);
  try {
    if (accessToken) localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    if (refreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  } catch (_) {}
}

export function getStoredAuthTokens() {
  try {
    if (!isTokenAuthEnabled()) {
      return { accessToken: null, refreshToken: null };
    }
    const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    return { accessToken, refreshToken };
  } catch (_) {
    return { accessToken: null, refreshToken: null };
  }
}

export function getUser() {
  const data = localStorage.getItem("user");
  try {
    return data ? JSON.parse(data) : null;
  } catch (err) {
    return null;
  }
}

function normalizeStoredEmail(value) {
  if (typeof value !== "string") return "";
  return value.trim().toLowerCase();
}

export function getPersistedMusaEmail() {
  const localUser = getUser();
  const resolved = [
    localStorage.getItem("musa_email"),
    localStorage.getItem("userEmail"),
    localUser?.email,
    localUser?.user?.email,
  ]
    .map(normalizeStoredEmail)
    .find(Boolean);

  if (!resolved) return "";

  try {
    if (localStorage.getItem("musa_email") !== resolved) {
      localStorage.setItem("musa_email", resolved);
    }
    if (localStorage.getItem("userEmail") !== resolved) {
      localStorage.setItem("userEmail", resolved);
    }
  } catch (_) {}

  return resolved;
}

export function hasStoredSessionIdentityHint() {
  try {
    return Boolean(
      [
        localStorage.getItem("userEmail"),
        localStorage.getItem("musa_email"),
      ]
        .map((value) => normalizeStoredEmail(value))
        .find(Boolean)
    );
  } catch (_) {
    return false;
  }
}

export async function logout(navigate) {
  markExplicitLogout();
  try {
    const headers = appendCsrfHeader({ Accept: "application/json" }, "POST");
    const logoutPaths = ["logout/", "core/logout/"]; // first is the canonical endpoint; second keeps backward compat
    let logoutCompleted = false;

    for (const path of logoutPaths) {
      try {
        const res = await fetch(resolveApiUrl(path), {
          method: "POST",
          credentials: "include",
          headers,
        });

        // Stop on success or when the backend already considers the session expired
        if (res.ok || res.status === 401 || res.status === 403) {
          logoutCompleted = true;
          break;
        }
      } catch (innerErr) {
        if (process.env.NODE_ENV !== "production") {
          console.warn(`Logout request failed for ${path}:`, innerErr);
        }
      }
    }

    if (!logoutCompleted && process.env.NODE_ENV !== "production") {
      console.warn("Logout endpoint did not respond with success; proceeding to clear client auth data.");
    }
  } catch (err) {
    if (process.env.NODE_ENV !== "production") {
      console.warn("Logout request failed:", err);
    }
  } finally {
    clearStoredAuthData({ preserveMusaEmail: false });
    if (navigate) {
      navigate("/", { replace: true });
    } else {
      window.location.href = "/";
    }
  }
}

export function isUserInRegistration() {
  const user = getUser();
  if (!user) return false;
  return !!resolveRegistrationRoute(user);
}

export function hasCompletedFullRegistration() {
  const user = getUser();
  if (!user) {
    return false;
  }

  const pendingRoute = resolveRegistrationRoute(user);
  if (!pendingRoute) {
    const role =
      typeof user.role === "string" ? user.role.toLowerCase() : user.role;
    if (role !== "cliente") return true;
  }

  const role =
    typeof user.role === "string" ? user.role.toLowerCase() : user.role;

  if (role === "cliente") {
    const completionFlag = resolveClientCompletionFlag(user);
    if (completionFlag !== null) {
      return completionFlag;
    }
    return isClientBasicsComplete(user);
  }

  if (role === "acompanhante") {
    const documentVerified =
      typeof user.document_verified === "string"
        ? user.document_verified.toLowerCase() === "true"
        : Boolean(user.document_verified);
    const profileCompleted =
      typeof user.profile_completed === "string"
        ? user.profile_completed.toLowerCase() === "true"
        : Boolean(user.profile_completed);
    return documentVerified && profileCompleted;
  }

  return false;
}

async function fetchProfile() {
  try {
    const response = await fetch(resolveApiUrl("profile/"), {
      method: "GET",
      credentials: "include",
      headers: { Accept: "application/json" },
    });
    return response;
  } catch (err) {
    if (process.env.NODE_ENV !== "production") {
      console.warn("Falha ao consultar perfil:", err);
    }
    return null;
  }
}

/**
 * Garante que existe uma sessão autenticada (via cookies) para o upload de documentos.
 * Se a sessão estiver expirada, tenta reautenticar usando o fluxo de pré-cadastro.
 */
export async function ensureDocumentUploadAuth() {
  const email = getPersistedMusaEmail();

  if (!email) {
    throw new Error("Email nao encontrado. Reinicie o processo de cadastro.");
  }

  const profileRes = await fetchProfile();
  if (profileRes?.ok) {
    let profileData = null;
    try {
      profileData = await profileRes.json();
    } catch (_) {
      profileData = null;
    }

    const localUser = getUser();
    const role = localUser?.role || profileData?.role || profileData?.user?.role;
    const profEmail =
      localUser?.email || profileData?.email || profileData?.user?.email;

    if (
      role === "acompanhante" &&
      (!profEmail ||
        email.toLowerCase() === String(profEmail).toLowerCase())
    ) {
      if (!localUser && profileData?.user) {
        localStorage.setItem("user", JSON.stringify(profileData.user));
      }
      return { success: true };
    }
  } else if (profileRes && profileRes.status !== 401 && profileRes.status !== 403) {
    const errorData = await profileRes
      .clone()
      .json()
      .catch(() => ({}));
    const reason =
      errorData?.detail ||
      errorData?.error ||
      "Falha ao validar a sessão atual.";
    throw new Error(reason);
  }

  const headers = appendCsrfHeader(
    { "Content-Type": "application/json", Accept: "application/json" },
    "POST"
  );

  const response = await fetch(resolveApiUrl("pre-cadastro-acompanhante/"), {
    method: "POST",
    credentials: "include",
    headers,
    body: JSON.stringify({ email }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error ||
        errorData.detail ||
        "Falha ao autenticar para upload de documentos."
    );
  }

  const data = await response.json().catch(() => ({}));
  if (data?.user) {
    const rawUser = data.user;
    const existingUser = getUser() || {};

    const parseStep = (value) => {
      if (typeof value === "number") return Number.isFinite(value) ? value : null;
      if (typeof value === "string" && value.trim()) {
        const parsed = Number.parseInt(value, 10);
        return Number.isFinite(parsed) ? parsed : null;
      }
      return null;
    };
    const coerceBool = (value) => {
      if (typeof value === "string") {
        const normalized = value.trim().toLowerCase();
        if (["true", "1", "yes", "sim"].includes(normalized)) return true;
        if (["false", "0", "no", "nao", "não", ""].includes(normalized)) return false;
      }
      return Boolean(value);
    };

    const existingSummary = existingUser?.progress_summary || {};
    const incomingSummary = rawUser?.progress_summary || {};

    const currentStep = parseStep(
      rawUser?.progress_step ??
      incomingSummary?.progress_step ??
      incomingSummary?.current_step ??
      data?.progress_step
    );
    const previousStep = parseStep(
      existingUser?.progress_step ??
      existingSummary?.progress_step ??
      existingSummary?.current_step
    );
    const resolvedStep = (() => {
      const candidates = [currentStep, previousStep, 4];
      const valid = candidates.filter((v) => Number.isFinite(v));
      return valid.length ? Math.max(...valid) : 4;
    })();

    const mergedSummary = {
      ...existingSummary,
      ...incomingSummary,
      progress_step: resolvedStep,
      current_step: resolvedStep,
      phone_verified: coerceBool(
        incomingSummary?.phone_verified ??
          rawUser?.phone_verified ??
          existingSummary?.phone_verified ??
          existingUser?.phone_verified
      ),
      document_verified: coerceBool(
        incomingSummary?.document_verified ??
          rawUser?.document_verified ??
          existingSummary?.document_verified ??
          existingUser?.document_verified
      ),
      profile_completed: coerceBool(
        incomingSummary?.profile_completed ??
          rawUser?.profile_completed ??
          existingSummary?.profile_completed ??
          existingUser?.profile_completed
      ),
    };

    const userWithProgress = {
      ...existingUser,
      ...rawUser,
      progress_step: resolvedStep,
      phone_verified: mergedSummary.phone_verified,
      document_verified: mergedSummary.document_verified,
      profile_completed: mergedSummary.profile_completed,
      progress_summary: mergedSummary,
    };

    localStorage.setItem("user", JSON.stringify(userWithProgress));
  }

  return { success: true };
}
